import React, { useState, useEffect, useCallback } from "react";
import { withNamespaces } from "react-i18next";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Progress,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Alert,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupText,
  Collapse,
} from "reactstrap";
import Select from "react-select";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import classnames from "classnames";
import Swal from "sweetalert2";

// Styles personnalisés pour React-Select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "8px",
    border: state.isFocused ? "2px solid #667eea" : "1px solid #e3e6f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,0.1)" : "none",
    "&:hover": {
      border: state.isFocused ? "2px solid #667eea" : "1px solid #d1d3e2",
    },
    minHeight: "38px",
    fontSize: "13px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#667eea"
      : state.isFocused
        ? "#f8f9fc"
        : "white",
    color: state.isSelected ? "white" : "#5a5c69",
    fontSize: "13px",
    padding: "8px 12px",
    cursor: "pointer",
  }),
};

// Styles des cards
const cardStyle = {
  borderRadius: "12px",
  border: "none",
  boxShadow: "0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15)",
};

const cardHeaderStyle = {
  backgroundColor: "#f8f9fc",
  borderBottom: "1px solid #e3e6f0",
  borderRadius: "12px 12px 0 0",
};

const IUCNDataSyncModal = ({ isOpen, onClose, t }) => {
  // États principaux
  const [activeTab, setActiveTab] = useState("sync");
  const [mode, setMode] = useState("dryRun");
  const [selectedZoo, setSelectedZoo] = useState(null);
  const [selectedSpecieId, setSelectedSpecieId] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // États pour l'analyse
  const [analysisData, setAnalysisData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // États pour les filtres
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  // Options de mode
  const modeOptions = [
    { value: "dryRun", label: "Dry Run", icon: "fa-search", description: "Simulation", color: "info" },
    { value: "singleSpecie", label: "Espèce unique", icon: "fa-paw", description: "Une seule espèce", color: "primary" },
    { value: "all", label: "Toutes", icon: "fa-globe", description: "Toutes les espèces", color: "danger" },
  ];

  // Options de filtre de statut
  const statusOptions = [
    { value: "all", label: "Tous les statuts" },
    { value: "to_fix", label: "À corriger" },
    { value: "fixed", label: "Corrigées" },
    { value: "skipped", label: "Ignorées" },
    { value: "error", label: "Erreurs" },
  ];

  // État pour la liste des espèces (chargée depuis Firestore)
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [isLoadingSpecies, setIsLoadingSpecies] = useState(false);

  // Charger la liste des espèces depuis Firestore
  const loadSpecies = useCallback(async () => {
    setIsLoadingSpecies(true);
    try {
      const db = firebase.firestore();
      const speciesSnapshot = await db.collection("species").get();

      const options = speciesSnapshot.docs.map(doc => ({
        value: doc.id,
        label: `${doc.data().specieName || doc.data().specieLatinName || doc.id} (${doc.data().specieLatinName || ""})`,
      })).sort((a, b) => a.label.localeCompare(b.label));

      setSpeciesOptions(options);
      console.log("[IUCNDataSync] Loaded", options.length, "species");
    } catch (err) {
      console.error("[IUCNDataSync] Error loading species:", err);
    } finally {
      setIsLoadingSpecies(false);
    }
  }, []);

  // Charger les espèces à l'ouverture de la modale
  useEffect(() => {
    if (isOpen && speciesOptions.length === 0) {
      loadSpecies();
    }
  }, [isOpen, speciesOptions.length, loadSpecies]);

  // Réinitialiser lors de la fermeture
  useEffect(() => {
    if (!isOpen) {
      setResult(null);
      setError(null);
      setExpandedRows({});
      setSelectedSpecieId("");
    }
  }, [isOpen]);

  // Analyser les données
  const analyzeData = useCallback(async () => {
    setIsAnalyzing(true);
    setAnalysisData(null);
    setError(null);

    try {
      const db = firebase.firestore();

      const speciesSnap = await db.collection("species").get();

      const analysis = {
        total: speciesSnap.size,
        withIUCN: 0,
        inconsistent: 0,
        consistent: 0,
        withoutIUCN: 0,
        samples: {
          inconsistent: [],
        }
      };

      speciesSnap.forEach(doc => {
        const data = doc.data();
        const hasIUCN = !!data.iucn;

        if (hasIUCN) {
          analysis.withIUCN++;
          const iucnObjectId = data.iucn?.id || data.iucn?.taxonid;
          const topLevelId = data.iucnId;

          if (iucnObjectId !== topLevelId) {
            analysis.inconsistent++;
            if (analysis.samples.inconsistent.length < 10) {
              analysis.samples.inconsistent.push({
                specieId: doc.id,
                specieName: data.specieName,
                specieLatinName: data.specieLatinName,
                iucnId: topLevelId,
                iucnObjectId: iucnObjectId
              });
            }
          } else {
            analysis.consistent++;
          }
        } else {
          analysis.withoutIUCN++;
        }
      });

      setAnalysisData(analysis);
    } catch (err) {
      console.error("[IUCNDataSync] Analysis error:", err);
      setError(err.message || "Erreur lors de l'analyse");
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  // Synchronisation IUCN
  const handleSync = async () => {
    setIsRunning(true);
    setResult(null);
    setError(null);

    try {
      const db = firebase.firestore();
      const isDryRun = mode === "dryRun";
      const migrationId = `iucn_sync_${moment().format("YYYYMMDD_HHmmss")}`;

      // Déterminer la query
      let query;
      if (mode === "singleSpecie" && selectedSpecieId) {
        const doc = await db.collection("species").doc(selectedSpecieId).get();
        query = { size: doc.exists ? 1 : 0, docs: doc.exists ? [doc] : [] };
      } else {
        query = await db.collection("species").get();
      }

      let toFix = 0;
      let fixed = 0;
      let alreadyOk = 0;
      let errors = 0;
      const details = [];
      const backupData = [];

      for (const doc of query.docs) {
        const data = doc.data();

        // Vérifier si l'espèce a des données IUCN
        if (!data.iucn) {
          alreadyOk++;
          continue;
        }

        const iucnObjectId = data.iucn?.id || data.iucn?.taxonid;
        const topLevelId = data.iucnId;

        // Vérifier la cohérence
        if (iucnObjectId === topLevelId) {
          alreadyOk++;
          continue; // ✅ VRAIMENT skip - on ne touche PAS au document
        }

        // Document incohérent - à corriger
        if (isDryRun) {
          toFix++;
          details.push({
            specieId: doc.id,
            specieName: data.specieName,
            specieLatinName: data.specieLatinName,
            status: "to_fix",
            change: {
              field: "iucnId",
              from: topLevelId,
              to: iucnObjectId
            }
          });
        } else {
          try {
            // Backup avant modification
            backupData.push({
              specieId: doc.id || "",
              specieName: data.specieName || "",
              currentIucnId: topLevelId || null,
              correctIucnId: iucnObjectId || null
            });

            // ⚠️ IMPORTANT: On ne met à jour QUE les espèces incohérentes
            // Cela évite de déclencher la Cloud Function pour les espèces déjà OK
            await db.collection("species").doc(doc.id).update({
              iucnId: iucnObjectId || null,
              _migrationMeta: {
                migrationId: migrationId,
                migratedAt: firebase.firestore.FieldValue.serverTimestamp(),
                action: "synced_iucnId",
                previousState: {
                  iucnId: topLevelId || null
                }
              }
            });

            fixed++;
            details.push({
              specieId: doc.id,
              specieName: data.specieName,
              specieLatinName: data.specieLatinName,
              status: "fixed",
              message: `iucnId synchronisé: ${topLevelId} → ${iucnObjectId}`,
              migrationId: migrationId
            });
          } catch (updateError) {
            errors++;
            details.push({
              specieId: doc.id,
              specieName: data.specieName,
              specieLatinName: data.specieLatinName,
              status: "error",
              error: updateError.message
            });
          }
        }
      }

      // Créer un backup si mode réel
      let backupId = null;
      if (!isDryRun && backupData.length > 0) {
        const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
        await db.collection("species_backup").doc(timestamp).set({
          timestamp: new Date().toISOString(),
          speciesCount: backupData.length,
          species: backupData
        });
        backupId = timestamp;
        console.log(`✅ Backup created: ${timestamp}`);
      }

      // Logger la migration
      if (!isDryRun) {
        await db.collection("logs").doc("iucn_migrations").collection("history").add({
          timestamp: new Date().toISOString(),
          dryRun: isDryRun,
          migrationId: migrationId,
          totalSpecies: query.size,
          fixed: fixed,
          failed: errors,
          mode: mode,
          selectedSpecieId: mode === "singleSpecie" ? (selectedSpecieId || null) : null,
          backupId: backupId || null,
          errors: details.filter(d => d.status === "error").map(e => ({
            specieId: e.specieId || "",
            specieName: e.specieName || "",
            message: e.error || ""
          })),
          changes: details.filter(d => d.status === "fixed").map(s => ({
            specieId: s.specieId || "",
            specieName: s.specieName || "",
            from: (s.change && s.change.from) || null,
            to: (s.change && s.change.to) || null
          }))
        });
        console.log("✅ Migration logged");
      }

      setResult({
        type: "sync",
        dryRun: isDryRun,
        migrationId: isDryRun ? null : migrationId,
        backupId: backupId,
        summary: {
          total: query.size,
          toFix: isDryRun ? toFix : 0,
          fixed: isDryRun ? 0 : fixed,
          alreadyOk,
          errors
        },
        details
      });

    } catch (err) {
      console.error("[IUCNDataSync] Sync error:", err);
      setError(err.message || "Erreur lors de la synchronisation");
    } finally {
      setIsRunning(false);
    }
  };

  // Rollback
  const handleRollback = async () => {
    // Confirmation
    const confirm = await Swal.fire({
      title: "⚠️ Rollback",
      html: `
        <p>Êtes-vous sûr de vouloir annuler la synchronisation ?</p>
        <p class="text-danger"><strong>Cette action restaurera les anciennes valeurs.</strong></p>
      `,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, rollback",
      cancelButtonText: "Annuler",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setIsRunning(true);
    setResult(null);
    setError(null);

    try {
      const db = firebase.firestore();
      const isDryRun = mode === "dryRun";

      // Chercher tous les documents avec _migrationMeta.action = "synced_iucnId"
      let query = db.collection("species").where("_migrationMeta.action", "==", "synced_iucnId");

      if (mode === "singleSpecie" && selectedSpecieId) {
        // On ne peut pas combiner where et doc(), donc on filtre après
      }

      const snapshot = await query.get();

      let toRollback = 0;
      let rolledBack = 0;
      let errors = 0;
      const details = [];

      for (const doc of snapshot.docs) {
        if (mode === "singleSpecie" && selectedSpecieId && doc.id !== selectedSpecieId) {
          continue;
        }

        const data = doc.data();

        if (isDryRun) {
          toRollback++;
          details.push({
            specieId: doc.id,
            specieName: data.specieName,
            specieLatinName: data.specieLatinName,
            migrationId: data._migrationMeta?.migrationId,
            status: "to_rollback",
            message: `iucnId sera restauré: ${data.iucnId} → ${data._migrationMeta?.previousState?.iucnId}`
          });
        } else {
          try {
            // Restaurer l'ancienne valeur
            await db.collection("species").doc(doc.id).update({
              iucnId: data._migrationMeta.previousState.iucnId,
              _migrationMeta: firebase.firestore.FieldValue.delete()
            });
            rolledBack++;
            details.push({
              specieId: doc.id,
              specieName: data.specieName,
              specieLatinName: data.specieLatinName,
              status: "rolled_back",
              message: "iucnId restauré"
            });
          } catch (updateError) {
            errors++;
            details.push({
              specieId: doc.id,
              specieName: data.specieName,
              specieLatinName: data.specieLatinName,
              status: "error",
              error: updateError.message
            });
          }
        }
      }

      setResult({
        type: "rollback",
        dryRun: isDryRun,
        summary: {
          total: toRollback + rolledBack + errors,
          toRollback: isDryRun ? toRollback : 0,
          rolledBack: isDryRun ? 0 : rolledBack,
          errors
        },
        details
      });

    } catch (err) {
      console.error("[IUCNDataSync] Rollback error:", err);
      setError(err.message || "Erreur lors du rollback");
    } finally {
      setIsRunning(false);
    }
  };

  // Vérifier si le bouton peut être cliqué
  const canRun = () => {
    if (isRunning) return false;
    if (mode === "singleSpecie" && !selectedSpecieId) return false;
    return true;
  };

  // Obtenir la couleur du badge selon le status
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "fixed":
      case "rolled_back": return "success";
      case "to_fix":
      case "to_rollback": return "info";
      case "skipped": return "warning";
      case "error": return "danger";
      default: return "secondary";
    }
  };

  // Obtenir le label du status
  const getStatusLabel = (status) => {
    switch (status) {
      case "fixed": return "Corrigée";
      case "to_fix": return "À corriger";
      case "rolled_back": return "Restaurée";
      case "to_rollback": return "À restaurer";
      case "skipped": return "Ignorée";
      case "error": return "Erreur";
      default: return status;
    }
  };

  // Filtrer les détails
  const getFilteredDetails = () => {
    if (!result || !result.details) return [];

    return result.details.filter(detail => {
      if (statusFilter !== "all" && detail.status !== statusFilter) return false;

      if (searchFilter) {
        const search = searchFilter.toLowerCase();
        const specieName = (detail.specieName || "").toLowerCase();
        const specieLatinName = (detail.specieLatinName || "").toLowerCase();
        const specieId = (detail.specieId || "").toLowerCase();

        if (!specieName.includes(search) && !specieLatinName.includes(search) && !specieId.includes(search)) {
          return false;
        }
      }

      return true;
    });
  };

  // Exporter les résultats
  const exportResults = (onlyErrors = false) => {
    if (!result || !result.details) return;

    let dataToExport = result.details;
    if (onlyErrors) {
      dataToExport = result.details.filter((detail) => detail.status === "error");
    }

    if (dataToExport.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    const exportData = {
      exportDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      type: result.type,
      dryRun: result.dryRun,
      migrationId: result.migrationId,
      backupId: result.backupId,
      summary: result.summary,
      details: dataToExport
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `iucn-sync-${result.type}-${onlyErrors ? "errors-" : ""}${moment().format("YYYY-MM-DD_HH-mm")}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Compter les erreurs
  const getErrorCount = () => {
    if (!result || !result.details) return 0;
    return result.details.filter((detail) => detail.status === "error").length;
  };

  // Toggle expand row
  const toggleExpandRow = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Render stat card
  const renderStatCard = (value, label, bgColor, textColor) => (
    <div style={{
      textAlign: "center",
      padding: "12px 8px",
      backgroundColor: bgColor,
      borderRadius: "8px",
      height: "100%"
    }}>
      <div style={{ fontSize: "24px", fontWeight: "bold", color: textColor }}>
        {value}
      </div>
      <small style={{ color: "#858796", fontSize: "11px" }}>{label}</small>
    </div>
  );

  // Render mode selector
  const renderModeSelector = () => (
    <Card className="mb-3" style={cardStyle}>
      <CardBody style={{ padding: "1rem" }}>
        <Row className="align-items-center">
          <Col md={8}>
            <div className="d-flex">
              {modeOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => !isRunning && setMode(option.value)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: mode === option.value ? `2px solid #667eea` : "1px solid #e3e6f0",
                    backgroundColor: mode === option.value ? "#f0f1ff" : "white",
                    cursor: isRunning ? "not-allowed" : "pointer",
                    marginRight: "10px",
                    textAlign: "center",
                    opacity: isRunning ? 0.6 : 1,
                  }}
                >
                  <i className={`fas ${option.icon} mr-2`} style={{
                    color: mode === option.value ? "#667eea" : "#858796"
                  }}></i>
                  <span style={{ fontSize: "13px", fontWeight: "500", color: "#5a5c69" }}>
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
          </Col>
          <Col md={4}>
            {mode === "singleSpecie" && (
              <Select
                styles={customSelectStyles}
                options={speciesOptions}
                value={speciesOptions.find(s => s.value === selectedSpecieId)}
                onChange={(e) => setSelectedSpecieId(e?.value || "")}
                placeholder={isLoadingSpecies ? "Chargement des espèces..." : "Choisir une espèce..."}
                isClearable
                isDisabled={isRunning || isLoadingSpecies}
                isLoading={isLoadingSpecies}
              />
            )}
          </Col>
        </Row>
        {mode === "all" && (
          <Alert color="warning" className="mt-2 mb-0 py-2">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <small><strong>Attention :</strong> Cette action affectera TOUTES les espèces.</small>
          </Alert>
        )}
      </CardBody>
    </Card>
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" style={{ maxWidth: "1100px" }}>
      <ModalHeader
        toggle={onClose}
        style={{
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
          color: "white",
        }}
      >
        <i className="fas fa-sync-alt mr-2"></i>
        Synchronisation IUCN Data
      </ModalHeader>

      <ModalBody style={{ padding: "0" }}>
        {/* Navigation par onglets */}
        <Nav tabs style={{ padding: "0 1.5rem", backgroundColor: "#f8f9fc" }}>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "sync" })}
              onClick={() => { setActiveTab("sync"); setResult(null); }}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-sync-alt mr-2"></i>
              Synchronisation
              <Badge color="primary" className="ml-2" pill>Principal</Badge>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "analysis" })}
              onClick={() => setActiveTab("analysis")}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-chart-pie mr-2"></i>
              Analyse
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "rollback" })}
              onClick={() => { setActiveTab("rollback"); setResult(null); }}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-undo mr-2"></i>
              Rollback
              <Badge color="danger" className="ml-2" pill>!</Badge>
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} style={{ padding: "1.5rem" }}>

          {/* Onglet Synchronisation */}
          <TabPane tabId="sync">
            <Alert color="info" className="mb-3">
              <i className="fas fa-info-circle mr-2"></i>
              <strong>Synchronisation IUCN</strong><br />
              <small>
                Cette migration synchronise le champ <code>iucnId</code> avec <code>iucn.id</code> dans les documents species.
                Les modifications sont automatiquement propagées aux animaux via Cloud Function.
              </small>
            </Alert>

            {renderModeSelector()}

            {/* Résultats de synchronisation */}
            {result && result.type === "sync" && (
              <Card style={cardStyle}>
                <CardHeader style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{ color: "#5a5c69", fontWeight: "600" }}>
                      <i className="fas fa-check-circle mr-2"></i>
                      Résultats de synchronisation
                      {result.dryRun && <Badge color="info" className="ml-2">Dry Run</Badge>}
                    </h6>
                    <div>
                      <Button color="secondary" size="sm" className="mr-2" onClick={() => exportResults(false)}>
                        <i className="fas fa-download mr-1"></i>
                        Tout
                      </Button>
                      {getErrorCount() > 0 && (
                        <Button color="warning" size="sm" onClick={() => exportResults(true)}>
                          <i className="fas fa-exclamation-triangle mr-1"></i>
                          Erreurs ({getErrorCount()})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {/* Migration ID pour rollback */}
                  {result.migrationId && (
                    <Alert color="success" className="mb-3 py-2">
                      <i className="fas fa-key mr-2"></i>
                      <strong>Migration ID :</strong>{" "}
                      <code style={{ backgroundColor: "#e8f5e9", padding: "2px 6px", borderRadius: "4px" }}>
                        {result.migrationId}
                      </code>
                      {result.backupId && (
                        <>
                          {" | "}
                          <strong>Backup ID :</strong>{" "}
                          <code style={{ backgroundColor: "#e8f5e9", padding: "2px 6px", borderRadius: "4px" }}>
                            {result.backupId}
                          </code>
                        </>
                      )}
                      <small className="ml-2 text-muted">(conservez ces IDs pour un éventuel rollback)</small>
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={3}>{renderStatCard(result.summary.total, "Total", "#f8f9fc", "#5a5c69")}</Col>
                    <Col md={3}>
                      {renderStatCard(
                        result.dryRun ? result.summary.toFix : result.summary.fixed,
                        result.dryRun ? "À corriger" : "Corrigées",
                        result.dryRun ? "#e3f2fd" : "#e8f5e9",
                        result.dryRun ? "#1976d2" : "#388e3c"
                      )}
                    </Col>
                    <Col md={3}>{renderStatCard(result.summary.alreadyOk, "Déjà OK", "#fff8e1", "#f9a825")}</Col>
                    <Col md={3}>
                      {renderStatCard(
                        result.summary.errors,
                        "Erreurs",
                        result.summary.errors > 0 ? "#ffebee" : "#f8f9fc",
                        result.summary.errors > 0 ? "#d32f2f" : "#858796"
                      )}
                    </Col>
                  </Row>

                  {/* Filtres */}
                  {result.details && result.details.length > 0 && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <Select
                          styles={customSelectStyles}
                          options={statusOptions}
                          value={statusOptions.find(o => o.value === statusFilter)}
                          onChange={(e) => setStatusFilter(e.value)}
                          placeholder={t("specie.iucn.filterPlaceholder")}
                        />
                      </Col>
                      <Col md={8}>
                        <InputGroup>
                          <InputGroupText style={{ backgroundColor: "white", borderRight: "none" }}>
                            <i className="fas fa-search" style={{ color: "#858796" }}></i>
                          </InputGroupText>
                          <Input
                            placeholder={t("specie.iucn.searchPlaceholder")}
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            style={{ borderLeft: "none" }}
                          />
                        </InputGroup>
                      </Col>
                    </Row>
                  )}

                  {/* Tableau des détails */}
                  {result.details && result.details.length > 0 && (
                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table size="sm" hover>
                        <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fc" }}>
                          <tr>
                            <th style={{ width: "25px" }}></th>
                            <th>Espèce</th>
                            <th>Nom latin</th>
                            <th>Status</th>
                            <th>Changement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredDetails().map((detail, index) => {
                            const hasDetails = detail.change || detail.error;

                            return (
                              <React.Fragment key={index}>
                                <tr
                                  style={{
                                    backgroundColor: detail.status === "error" ? "#ffebee" : "inherit",
                                    cursor: hasDetails ? "pointer" : "default"
                                  }}
                                  onClick={() => hasDetails && toggleExpandRow(index)}
                                >
                                  <td>
                                    {hasDetails && (
                                      <i className={`fas fa-chevron-${expandedRows[index] ? 'down' : 'right'}`}
                                         style={{ color: "#858796", fontSize: "11px" }}></i>
                                    )}
                                  </td>
                                  <td><small>{detail.specieName || "-"}</small></td>
                                  <td><small><em>{detail.specieLatinName || "-"}</em></small></td>
                                  <td>
                                    <Badge color={getStatusBadgeColor(detail.status)} style={{ fontSize: "10px" }}>
                                      {getStatusLabel(detail.status)}
                                    </Badge>
                                  </td>
                                  <td><small>{detail.message || "-"}</small></td>
                                </tr>
                                {hasDetails && (
                                  <tr>
                                    <td colSpan="5" style={{ padding: 0, border: "none" }}>
                                      <Collapse isOpen={expandedRows[index]}>
                                        <div style={{
                                          padding: "8px 12px",
                                          backgroundColor: "#f8f9fc",
                                          fontSize: "11px"
                                        }}>
                                          {detail.error && (
                                            <Alert color="danger" className="mb-2 py-1">
                                              <small><strong>Erreur:</strong> {detail.error}</small>
                                            </Alert>
                                          )}
                                          {detail.change && (
                                            <div>
                                              <strong>Changement :</strong> <code>{detail.change.field}</code>
                                              <br />
                                              <Badge color="danger" className="mr-1">{detail.change.from || "null"}</Badge>
                                              <i className="fas fa-arrow-right mx-1"></i>
                                              <Badge color="success">{detail.change.to}</Badge>
                                            </div>
                                          )}
                                        </div>
                                      </Collapse>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Erreur */}
            {error && activeTab === "sync" && (
              <Alert color="danger" className="mt-3">
                <i className="fas fa-exclamation-circle mr-2"></i>
                <strong>Erreur :</strong> {error}
              </Alert>
            )}

            {/* Loader */}
            {isRunning && activeTab === "sync" && (
              <Card className="mt-3" style={cardStyle}>
                <CardBody className="text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status"></div>
                  <p style={{ color: "#5a5c69" }}>
                    {mode === "dryRun" ? "Analyse en cours..." : "Synchronisation en cours..."}
                  </p>
                  <Progress animated value={100} color="primary" style={{ height: "6px" }} />
                </CardBody>
              </Card>
            )}
          </TabPane>

          {/* Onglet Analyse */}
          <TabPane tabId="analysis">
            <Card style={cardStyle}>
              <CardHeader style={cardHeaderStyle}>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0" style={{ color: "#5a5c69", fontWeight: "600" }}>
                    <i className="fas fa-chart-pie mr-2"></i>
                    Analyse de la collection species
                  </h6>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={analyzeData}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <><span className="spinner-border spinner-border-sm mr-2"></span>Analyse...</>
                    ) : (
                      <><i className="fas fa-sync-alt mr-2"></i>Analyser</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {!analysisData && !isAnalyzing && (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-database" style={{ fontSize: "48px", marginBottom: "15px", opacity: 0.5 }}></i>
                    <p>Cliquez sur "Analyser" pour scanner la collection</p>
                  </div>
                )}

                {isAnalyzing && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status"></div>
                    <p className="text-muted">Analyse en cours...</p>
                  </div>
                )}

                {analysisData && (
                  <>
                    <Row className="mb-4">
                      <Col md={3}>{renderStatCard(analysisData.total, "Total espèces", "#f8f9fc", "#5a5c69")}</Col>
                      <Col md={3}>{renderStatCard(analysisData.consistent, "Cohérentes", "#e8f5e9", "#388e3c")}</Col>
                      <Col md={3}>
                        {renderStatCard(
                          analysisData.inconsistent,
                          "Incohérentes",
                          analysisData.inconsistent > 0 ? "#ffebee" : "#f8f9fc",
                          analysisData.inconsistent > 0 ? "#d32f2f" : "#858796"
                        )}
                      </Col>
                      <Col md={3}>{renderStatCard(analysisData.withoutIUCN, "Sans IUCN", "#f8f9fc", "#858796")}</Col>
                    </Row>

                    {/* Échantillons d'incohérences */}
                    {analysisData.samples.inconsistent.length > 0 && (
                      <Alert color="danger">
                        <strong><i className="fas fa-exclamation-triangle mr-2"></i>Échantillons incohérents :</strong>
                        <div style={{ maxHeight: "200px", overflowY: "auto", marginTop: "10px" }}>
                          <Table size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Espèce</th>
                                <th>Nom latin</th>
                                <th>iucnId</th>
                                <th>iucn.id</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analysisData.samples.inconsistent.map((sample, i) => (
                                <tr key={i}>
                                  <td><small>{sample.specieName}</small></td>
                                  <td><small><em>{sample.specieLatinName}</em></small></td>
                                  <td><Badge color="danger">{sample.iucnId || "null"}</Badge></td>
                                  <td><Badge color="success">{sample.iucnObjectId || "null"}</Badge></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Alert>
                    )}

                    {analysisData.inconsistent === 0 && (
                      <Alert color="success">
                        <i className="fas fa-check-circle mr-2"></i>
                        <strong>Parfait !</strong> Toutes les espèces avec données IUCN sont cohérentes.
                      </Alert>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </TabPane>

          {/* Onglet Rollback */}
          <TabPane tabId="rollback">
            <Alert color="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>Zone de danger - Rollback</strong><br />
              <small>
                Cette action annule les synchronisations effectuées. Utilisez avec précaution.
                Seules les données synchronisées avec les métadonnées de traçabilité peuvent être annulées.
              </small>
            </Alert>

            {renderModeSelector()}

            <Card style={{ ...cardStyle, borderTop: "3px solid #dc3545" }}>
              <CardHeader style={cardHeaderStyle}>
                <h6 className="mb-0" style={{ color: "#5a5c69", fontWeight: "600" }}>
                  <i className="fas fa-undo mr-2" style={{ color: "#dc3545" }}></i>
                  Rollback Synchronisation IUCN
                </h6>
              </CardHeader>
              <CardBody>
                <p className="text-muted" style={{ fontSize: "13px" }}>
                  Restaure les anciennes valeurs de <code>iucnId</code> pour les espèces synchronisées
                  (uniquement celles avec <code>_migrationMeta</code>).
                </p>
                <Button
                  color="danger"
                  block
                  onClick={handleRollback}
                  disabled={isRunning || (mode === "singleSpecie" && !selectedSpecieId)}
                >
                  {isRunning && result?.type === "rollback" ? (
                    <><span className="spinner-border spinner-border-sm mr-2"></span>En cours...</>
                  ) : (
                    <>
                      <i className={`fas ${mode === "dryRun" ? "fa-search" : "fa-undo"} mr-2`}></i>
                      {mode === "dryRun" ? "Analyser le rollback" : "Rollback synchronisation"}
                    </>
                  )}
                </Button>
              </CardBody>
            </Card>

            {/* Résultats du rollback */}
            {result && result.type === "rollback" && (
              <Card className="mt-3" style={cardStyle}>
                <CardHeader style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{ color: "#5a5c69", fontWeight: "600" }}>
                      <i className="fas fa-undo mr-2"></i>
                      Résultats du rollback
                      {result.dryRun && <Badge color="info" className="ml-2">Dry Run</Badge>}
                    </h6>
                    <Button color="secondary" size="sm" onClick={() => exportResults(false)}>
                      <i className="fas fa-download mr-1"></i>
                      Exporter
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={4}>{renderStatCard(result.summary.total, "Total trouvés", "#f8f9fc", "#5a5c69")}</Col>
                    <Col md={4}>
                      {renderStatCard(
                        result.dryRun ? result.summary.toRollback : result.summary.rolledBack,
                        result.dryRun ? "À restaurer" : "Restaurées",
                        result.dryRun ? "#fff8e1" : "#e8f5e9",
                        result.dryRun ? "#f9a825" : "#388e3c"
                      )}
                    </Col>
                    <Col md={4}>
                      {renderStatCard(
                        result.summary.errors,
                        "Erreurs",
                        result.summary.errors > 0 ? "#ffebee" : "#f8f9fc",
                        result.summary.errors > 0 ? "#d32f2f" : "#858796"
                      )}
                    </Col>
                  </Row>

                  {result.details && result.details.length > 0 && (
                    <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                      <Table size="sm" hover>
                        <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fc" }}>
                          <tr>
                            <th>Espèce</th>
                            <th>Nom latin</th>
                            <th>Status</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.details.map((detail, index) => (
                            <tr key={index} style={{
                              backgroundColor: detail.status === "error" ? "#ffebee" : "inherit"
                            }}>
                              <td>{detail.specieName || "-"}</td>
                              <td><em>{detail.specieLatinName || "-"}</em></td>
                              <td>
                                <Badge
                                  color={getStatusBadgeColor(detail.status)}
                                  style={{ fontSize: "10px" }}
                                >
                                  {getStatusLabel(detail.status)}
                                </Badge>
                              </td>
                              <td><small>{detail.message || detail.error || "-"}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {result.details && result.details.length === 0 && (
                    <Alert color="info" className="mb-0">
                      <i className="fas fa-info-circle mr-2"></i>
                      Aucun document à restaurer. Les synchronisations précédentes n'ont peut-être pas inclus les métadonnées de traçabilité.
                    </Alert>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Erreur */}
            {error && activeTab === "rollback" && (
              <Alert color="danger" className="mt-3">
                <i className="fas fa-exclamation-circle mr-2"></i>
                <strong>Erreur :</strong> {error}
              </Alert>
            )}

            {/* Loader */}
            {isRunning && activeTab === "rollback" && (
              <Card className="mt-3" style={cardStyle}>
                <CardBody className="text-center py-4">
                  <div className="spinner-border text-warning mb-3" role="status"></div>
                  <p style={{ color: "#5a5c69" }}>Rollback en cours...</p>
                  <Progress animated value={100} color="warning" style={{ height: "6px" }} />
                </CardBody>
              </Card>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter style={{ borderTop: "1px solid #e3e6f0", backgroundColor: "#f8f9fc" }}>
        <Button color="secondary" onClick={onClose} disabled={isRunning}>
          <i className="fas fa-times mr-2"></i>
          Fermer
        </Button>
        {activeTab === "sync" && (
          <Button
            color={mode === "dryRun" ? "info" : mode === "all" ? "danger" : "primary"}
            onClick={handleSync}
            disabled={!canRun()}
          >
            {isRunning ? (
              <><span className="spinner-border spinner-border-sm mr-2"></span>En cours...</>
            ) : (
              <>
                <i className={`fas ${mode === "dryRun" ? "fa-search" : "fa-sync-alt"} mr-2`}></i>
                {mode === "dryRun" ? "Analyser la synchronisation" : "Synchroniser"}
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default withNamespaces("translations")(IUCNDataSyncModal);
